import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '向日葵视频',
  url: 'https://www.xiangrikui-video.com/',
  description: '向日葵视频提供免费高清在线观看，榴莲视频、红杏视频等热播电影电视剧综艺全集，流畅不缓冲，在线播放体验佳。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.xiangrikui-video.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '向日葵视频',
  url: 'https://www.xiangrikui-video.com/',
  description: '向日葵视频提供免费高清在线观看服务',
  sameAs: [
    'https://weibo.com/xiangrikui',
    'https://www.bilibili.com/',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '向日葵视频真的免费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的！向日葵视频所有内容完全免费在线观看，无需注册，无需付费，打开即可享受高清流畅的观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '榴莲视频和红杏视频有什么区别？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '榴莲视频专注于都市生活、美食探访、社会观察等原创内容；红杏视频则以浪漫爱情、青春校园等剧情类内容为特色。两者各有风格，都在向日葵视频平台免费提供。',
      },
    },
    {
      '@type': 'Question',
      name: '为什么播放不缓冲？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '向日葵视频采用先进的视频编码技术和CDN加速网络，智能适配用户网络环境，自动选择最佳清晰度，确保流畅不缓冲的播放体验。',
      },
    },
    {
      '@type': 'Question',
      name: '支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '向日葵视频支持电脑、平板、手机等多端观看，兼容主流浏览器。推荐使用Chrome或Edge浏览器获得最佳体验。',
      },
    },
  ],
};

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = activeCategory === 'all'
    ? videos
    : videos.filter((v) => v.categorySlug === activeCategory);

  const featuredVideos = videos.slice(0, 4);
  const latestArticles = articles.slice(0, 3);

  return (
    <SEO
      title="首页"
      description="向日葵视频提供免费高清在线观看，榴莲视频、红杏视频等热播电影电视剧综艺全集，流畅不缓冲，在线播放体验佳。"
      keywords="向日葵视频,榴莲视频,红杏视频,免费在线观看,高清,电影电视剧全集,不缓冲,在线播放,综艺"
      canonicalUrl="https://www.xiangrikui-video.com/"
      schemaData={{ ...homeSchema, ...orgSchema }}
    >
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main>
          <HeroSection />
          <FeaturedSection videos={featuredVideos} />
          <CategorySection
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            videos={filteredVideos}
          />
          <ArticleSection articles={latestArticles} />
          <StatsSection />
          <FAQSection faqSchema={faqSchema} />
          <CTASection />
        </main>
        <Footer />
      </div>
    </SEO>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20cinematic%20warm%20toned%20abstract%20background%20with%20soft%20golden%20light%20rays%2C%20elegant%20gradient%20texture%2C%20minimalist%20yet%20dramatic%2C%20film%20projector%20light%20effect%2C%20warm%20amber%20and%20coral%20tones%2C%20artistic%20photography&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
        alt="向日葵视频 - 免费高清在线观看"
        className="absolute inset-0 w-full h-full object-cover object-top"
        title="向日葵视频 - 榴莲视频、红杏视频免费高清在线观看"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/50 via-background-950/30 to-background-950/70" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          向日葵视频
        </h1>
        <p className="text-base md:text-xl text-background-200 max-w-2xl mb-8 leading-relaxed">
          榴莲视频 · 红杏视频 · 电影电视剧综艺全集<br />
          <strong>免费高清在线观看，流畅不缓冲</strong>
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/category/all"
            className="whitespace-nowrap px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-300 text-sm"
          >
            <i className="ri-play-circle-line mr-2" />
            立即观看
          </Link>
          <Link
            to="/about"
            className="whitespace-nowrap px-8 py-3 bg-background-50/20 hover:bg-background-50/30 text-background-50 font-medium rounded-full backdrop-blur-sm transition-all duration-300 text-sm border border-background-50/30"
          >
            <i className="ri-information-line mr-2" />
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedSection({ videos: featuredVideos }: { videos: typeof import('@/mocks/videos').videos }) {
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              <i className="ri-fire-line text-accent-500 mr-2" />
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">精选最受欢迎的精彩内容</p>
          </div>
          <Link to="/category/all" className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200">
            查看全部 <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featuredVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategorySection({
  categories: catList,
  activeCategory,
  onCategoryChange,
  videos: catVideos,
}: {
  categories: typeof import('@/mocks/videos').categories;
  activeCategory: string;
  onCategoryChange: (s: string) => void;
  videos: typeof import('@/mocks/videos').videos;
}) {
  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            内容分类
          </h2>
          <p className="text-sm text-foreground-500 mt-1">按分类浏览你感兴趣的内容</p>
        </div>

        {/* Segmented Control */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-background-200/60 rounded-full px-1 py-1 flex-wrap gap-1 justify-center">
            {catList.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => onCategoryChange(cat.slug)}
                className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all duration-200 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 font-medium shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-50'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {catVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {catVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-inbox-line text-4xl mb-3 block" />
            <p className="text-sm">该分类暂无内容，敬请期待</p>
          </div>
        )}
      </div>
    </section>
  );
}

function ArticleSection({ articles: latestArticles }: { articles: typeof import('@/mocks/articles').articles }) {
  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              <i className="ri-article-line text-secondary-500 mr-2" />
              最新文章
            </h2>
            <p className="text-sm text-foreground-500 mt-1">影视资讯、观影指南、行业动态</p>
          </div>
          <Link to="/article/a1" className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200">
            更多文章 <i className="ri-arrow-right-line ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {latestArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  const stats = [
    { value: '5000+', label: '高清影片', icon: 'ri-film-line' },
    { value: '99.9%', label: '播放流畅率', icon: 'ri-speed-line' },
    { value: '1000万+', label: '活跃用户', icon: 'ri-user-line' },
    { value: '24/7', label: '不间断服务', icon: 'ri-timer-line' },
  ];

  return (
    <section className="py-12 md:py-16 bg-accent-500">
      <div className="container-main">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center text-background-50">
              <div className="w-12 h-12 mx-auto mb-3 bg-background-50/20 rounded-full flex items-center justify-center">
                <i className={`${stat.icon} text-xl`} />
              </div>
              <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
              <p className="text-sm text-background-200 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection({ faqSchema }: { faqSchema: Record<string, unknown> }) {
  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="container-main max-w-[800px]">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            常见问题
          </h2>
        </div>
        <div className="space-y-3">
          {[
            { q: '向日葵视频真的免费吗？', a: '是的！向日葵视频所有内容完全免费在线观看，无需注册，无需付费，打开即可享受高清流畅的观影体验。' },
            { q: '榴莲视频和红杏视频有什么区别？', a: '榴莲视频专注于都市生活、美食探访、社会观察等原创内容；红杏视频则以浪漫爱情、青春校园等剧情类内容为特色。两者各有风格，都在向日葵视频平台免费提供。' },
            { q: '为什么播放不缓冲？', a: '向日葵视频采用先进的视频编码技术和CDN加速网络，智能适配用户网络环境，自动选择最佳清晰度，确保流畅不缓冲的播放体验。' },
            { q: '支持哪些设备观看？', a: '向日葵视频支持电脑、平板、手机等多端观看，兼容主流浏览器。推荐使用Chrome或Edge浏览器获得最佳体验。' },
          ].map((faq, idx) => (
            <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
              <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                <h4 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h4>
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 text-lg shrink-0" />
              </summary>
              <div className="px-4 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-16 md:py-20 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20gradient%20background%20with%20soft%20flowing%20light%20streaks%2C%20golden%20and%20coral%20tones%2C%20cinematic%20atmosphere%2C%20minimalist%20elegant%20texture&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-background-950/60" />
      <div className="relative container-main text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          准备好了吗？立即开始免费观影
        </h2>
        <p className="text-background-300 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
          加入向日葵视频，畅享榴莲视频、红杏视频等海量高清内容，流畅不缓冲，随时随地想看就看。
        </p>
        <Link
          to="/category/all"
          className="whitespace-nowrap inline-flex items-center px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all duration-300 text-sm"
        >
          <i className="ri-play-circle-line mr-2" />
          开始免费观看
        </Link>
      </div>
    </section>
  );
}